import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useLocation} from "react-router-dom";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {flexAbsoluteCenter, flexAlignCenter, pl, zIndex} from "@pg-design/helpers-css";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons";

import {changeLanguage} from "../../../../../../utils/change_language";
import {useAppDispatch} from "../../../../../../utils/hooks/store_hooks";
import {chartsRoutes} from "../../../../common/app/routing/charts_routes";
import {getMenuItems} from "../../../../common/app/routing/menu_items";
import {OnboardingTrigger} from "../../../../features/analytical_platform/components/onboarding/OnboardingTrigger";
import {useEffectiveCitySlug} from "../../../../features/analytical_platform/hooks/use_effective_city_slug";
import {toggleOnboarding} from "../../../../features/analytical_platform/redux/onboarding_slice";
import {useLogout} from "../../../../features/auth/hooks/useLogout";
import {UserAccountButtons} from "../../../../features/users/components/UserAccountButtons";
import {StyledIconWrapper} from "../../../StyledIconWrapper";
import {NavigationText} from "./NavigationText";

interface IProps {
    isOpen: boolean;
    setOpen: () => void;
    setClose: () => void;
    className?: string;
    hideAuth?: boolean;
}

export const BurgerMenu = ({isOpen, setOpen, setClose, className, hideAuth = false}: IProps) => {
    const {t, i18n} = useTranslation();
    const [isSolutionsSubmenuOpen, setIsSolutionsSubmenuOpen] = useState(false);
    const [isKnowledgeBaseSubmenuOpen, setIsKnowledgeBaseSubmenuOpen] = useState(false);
    const [isAccountSubmenuOpen, setIsAccountSubmenuOpen] = useState(false);

    const {pathname} = useLocation();
    const citySlug = useEffectiveCitySlug();
    const logout = useLogout();

    const handleLogout = () => {
        logout();
        setIsAccountSubmenuOpen(false);
    };

    const userMenuItems = useMemo(
        () => [
            {
                label: t("nav.profile"),
                route: chartsRoutes[i18n.language].user.edit
            },
            {
                label: t("nav.change_password"),
                route: chartsRoutes[i18n.language].user.changePassword
            },
            {
                label: t("nav.logout"),
                route: chartsRoutes[i18n.language].root,
                callback: handleLogout
            }
        ],
        [i18n.language]
    );

    return (
        <div css={wrapper} className={className}>
            <BurgerButtonWrapper
                onClick={(e) => {
                    e.preventDefault();
                    setIsSolutionsSubmenuOpen(false);
                    setIsKnowledgeBaseSubmenuOpen(false);
                    setIsAccountSubmenuOpen(false);
                    isOpen ? setClose() : setOpen();
                }}
                isOpen={isOpen}
            >
                <BurgerButton isOpen={isOpen} />
            </BurgerButtonWrapper>

            <StyledBackground isOpen={isOpen} />

            <StyledMenuHolder isOpen={isOpen}>
                <StyledMainMenu
                    isSubmenuOpen={isAccountSubmenuOpen || isSolutionsSubmenuOpen || isKnowledgeBaseSubmenuOpen}
                >
                    <li
                        css={[
                            label,
                            css`
                                padding-left: 3rem;
                            `
                        ]}
                    >
                        <NavigationText variant="body_copy_2">Menu</NavigationText>
                    </li>

                    <li>
                        <Link css={link} to={chartsRoutes[i18n.language].root}>
                            <NavigationText variant="info_txt_1">{t("nav.solutions")}</NavigationText>

                            <StyledIconWrapper
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsKnowledgeBaseSubmenuOpen(false);
                                    setIsSolutionsSubmenuOpen(true);
                                }}
                            >
                                <ChevronRightIcon size="1.5" />
                            </StyledIconWrapper>
                        </Link>
                    </li>

                    <li>
                        <Link css={link} to={chartsRoutes[i18n.language].newshub.marketInformation.root}>
                            <NavigationText variant="info_txt_1">{t("nav.insights")}</NavigationText>
                        </Link>
                    </li>

                    <li>
                        <Link css={link} to={chartsRoutes[i18n.language].pricing}>
                            <NavigationText variant="info_txt_1">{t("nav.pricing")}</NavigationText>
                        </Link>
                    </li>

                    <li>
                        <Link css={link} to={chartsRoutes[i18n.language].contact}>
                            <NavigationText variant="info_txt_1">{t("nav.contact")}</NavigationText>
                        </Link>
                    </li>

                    {!hideAuth && (
                        <li>
                            <UserAccountButtons onMobileClick={() => setIsAccountSubmenuOpen(true)} />
                        </li>
                    )}
                </StyledMainMenu>

                <StyledSubMenu isOpen={isSolutionsSubmenuOpen}>
                    <li
                        css={label}
                        onClick={() => {
                            setIsSolutionsSubmenuOpen(false);
                        }}
                    >
                        <ChevronLeftIcon size="1.5" />
                        <NavigationText variant="body_copy_2">{t("nav.solutions")}</NavigationText>
                    </li>

                    <li key="platform">
                        <Link css={link} to={chartsRoutes[i18n.language].solutionsAnalyticalPlatform}>
                            <NavigationText variant="info_txt_1">{t("landing.solutions.card1.title")}</NavigationText>
                        </Link>
                    </li>

                    <li key="monitoring">
                        <Link css={link} to={chartsRoutes[i18n.language].monitoring.root}>
                            <NavigationText variant="info_txt_1">{t("landing.solutions.card2.title")}</NavigationText>
                        </Link>
                    </li>
                </StyledSubMenu>

                <StyledSubMenu isOpen={isKnowledgeBaseSubmenuOpen}>
                    <li
                        css={label}
                        onClick={() => {
                            setIsKnowledgeBaseSubmenuOpen(false);
                        }}
                    >
                        <ChevronLeftIcon size="1.5" />
                        <NavigationText variant="body_copy_2">{t("nav.insights")}</NavigationText>
                    </li>
                </StyledSubMenu>

                {!hideAuth && (
                    <StyledSubMenu isOpen={isAccountSubmenuOpen}>
                        <li css={label} onClick={() => setIsAccountSubmenuOpen(false)}>
                            <ChevronLeftIcon size="1.5" />
                            <NavigationText variant="info_txt_1">{t("nav.account")}</NavigationText>
                        </li>

                        {userMenuItems.map((item) => (
                            <li key={item.label} onClick={item?.callback}>
                                <Link css={link} to={item.route}>
                                    <NavigationText variant="info_txt_1">{item.label}</NavigationText>
                                </Link>
                            </li>
                        ))}
                    </StyledSubMenu>
                )}
            </StyledMenuHolder>
        </div>
    );
};

const wrapper = (theme: Theme) => css`
    padding-left: 1rem;
    height: var(--mobile-header-height);
    ${flexAlignCenter};
    justify-self: end;

    @media (min-width: ${theme.breakpoints.lg}) {
        display: none;
    }
`;

const BurgerButtonWrapper = styled.div<{isOpen: boolean}>`
    ${flexAbsoluteCenter};
    height: var(--mobile-header-height);
    width: var(--mobile-header-height);
    background-color: ${(props) => props.isOpen && props.theme.colors.gray[200]};
    ${zIndex(9999)};

    ${(props) =>
        props.isOpen &&
        css`
            position: fixed;
            top: 0;
            right: 0;
            ${zIndex(10002)};
        `}
`;

const BurgerButton = styled.button<{isOpen: boolean}>`
    border: none;
    cursor: pointer;
    position: relative;
    width: 20px;
    height: 20px;
    background: transparent;
    border-top: 3px solid;
    border-bottom: 3px solid;
    ${zIndex(10003)};
    z-index: 103;
    color: ${(props) => props.theme.colors.secondary};
    transition: var(--transition);

    &:focus {
        outline: none;
    }

    &:before,
    &:after {
        content: "";
        display: block;
        width: 100%;
        height: 3px;
        position: absolute;
        top: 50%;
        left: 50%;
        background: currentColor;
        transform: translate(-50%, -50%);
        transition: (--transition);
    }

    ${(props) =>
        props.isOpen &&
        css`
            border-color: transparent;

            &:before {
                transform: translate(-50%, -50%) rotate(45deg);
            }

            &:after {
                transform: translate(-50%, -50%) rotate(-45deg);
            }
        `}
`;

const StyledBackground = styled.div<{isOpen: boolean}>`
    display: ${(props) => (props.isOpen ? "block" : "none")};
    width: 100vw;
    height: 100vh;
    background-color: black;
    position: fixed;
    overflow: hidden;
    right: 0;
    top: 0;
    ${zIndex(9999)};
    transition: var(--transition);
    animation: fade-in 0.3s cubic-bezier(0.39, 0.575, 0.565, 1) both;

    @keyframes fade-in {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 0.5;
        }
    }
`;

const label = (theme: Theme) => css`
    ${pl(3)};
    ${zIndex(9999)};
    height: var(--mobile-header-height);
    ${flexAlignCenter};
    background-color: ${theme.colors.gray[100]};
    gap: 2.4rem;
`;

const StyledMenuHolder = styled.div<{isOpen: boolean}>`
    position: fixed;
    right: 0;
    top: 0;
    ${zIndex(10000)};
    display: flex;
    width: ${(props) => (props.isOpen ? "100vw" : "0")};
    max-width: 360px;
    height: 100vh;
    overflow: hidden;
    transition: var(--transition);
    background-color: #fff;
`;

const menu = css`
    width: 100vw;
    max-width: 360px;
    height: 100vh;
    overflow: hidden;
    transition: var(--transition);
    background-color: #fff;
`;

const StyledMainMenu = styled.ul<{isSubmenuOpen: boolean}>`
    ${menu};
    transform: ${(props) => (props.isSubmenuOpen ? "translateX(-360px)" : "0")};
`;

const StyledSubMenu = styled.ul<{isOpen: boolean}>`
    position: fixed;
    ${menu};
    transform: ${(props) => (props.isOpen ? "0" : "translateX(360px)")};
`;

const link = css`
    height: 60px;
    ${pl(2)};
    ${flexAlignCenter};
    justify-content: space-between;
    white-space: nowrap;
`;
